import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { Award, Plus, Download, Trash2, FileText, Eye, Sparkles, Send, CheckCircle, Mail, MessageCircle } from 'lucide-react';
import { SendReportButton } from '../components/common/SendReportButton';
import EmailDocDialog from '../components/documents/EmailDocDialog';
import { openWhatsApp } from '../utils/whatsapp';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const ExperienceCertificate = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [emailCert, setEmailCert] = useState(null);
  const [contacts, setContacts] = useState({});
  const [searchParams] = useSearchParams();
  const presetEmployeeId = searchParams.get('employee_id') || '';
  const requestId = searchParams.get('request_id') || '';
  useEffect(() => { if (presetEmployeeId) setShowForm(true); }, [presetEmployeeId]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/experience-certificates`);
      setList(r.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    axios.get(`${API}/api/employees/contacts`)
      .then(r => setContacts(Object.fromEntries((r.data || []).map(c => [c.id, c]))))
      .catch(() => {});
  }, []);

  const waNumber = (empId) => {
    const c = contacts[empId] || {};
    return (c.whatsapp_no || c.phone || '').trim();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this certificate from history?')) return;
    await axios.delete(`${API}/api/experience-certificates/${id}`);
    toast({ title: 'Deleted' });
    load();
  };

  const download = (id) => {
    window.open(`${API}/api/experience-certificates/${id}/pdf`, '_blank');
  };

  const share = async (id) => {
    if (!window.confirm('Send this certificate to the staff portal? They will receive a notification and can download it.')) return;
    try {
      await axios.post(`${API}/api/experience-certificates/${id}/share`);
      toast({ title: 'Sent to employee portal', description: 'Staff has been notified.' });
      load();
    } catch (e) {
      toast({ title: 'Share failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const unshare = async (id) => {
    if (!window.confirm('Remove this certificate from the staff portal?')) return;
    await axios.post(`${API}/api/experience-certificates/${id}/unshare`);
    toast({ title: 'Revoked from portal' });
    load();
  };

  return (
    <div className="space-y-4" data-testid="experience-certificate-page">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Award className="h-7 w-7 text-amber-600" /> Experience Certificate
          </h1>
          <p className="text-gray-600 mt-1">Generate a polished employment certificate for any current or former staff.</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="cert-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New Certificate
        </Button>
      </div>

      {showForm && (
        <CertificateForm
          presetEmployeeId={presetEmployeeId}
          requestId={requestId}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Issued Certificates</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-gray-500">Loading…</div>
          ) : list.length === 0 ? (
            <div className="py-10 text-center text-gray-500">No certificates issued yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-amber-50 text-amber-800 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3">Reference</th>
                  <th className="text-left p-3">Employee</th>
                  <th className="text-left p-3">Designation</th>
                  <th className="text-left p-3">Period</th>
                  <th className="text-left p-3">Issued</th>
                  <th className="text-left p-3">Signatory</th>
                  <th className="text-left p-3">Portal</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-amber-50/30" data-testid={`cert-row-${c.id}`}>
                    <td className="p-3 font-mono text-xs">{c.reference_number}</td>
                    <td className="p-3 font-semibold">{c.employee_name}</td>
                    <td className="p-3 text-gray-600">{c.designation}</td>
                    <td className="p-3 text-xs">
                      {formatDate(c.date_of_joining)} → {c.is_currently_working ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Currently Working</Badge> : formatDate(c.last_working_date)}
                    </td>
                    <td className="p-3 text-xs">{formatDate(c.issue_date)}</td>
                    <td className="p-3 text-xs text-gray-600">{c.signatory_name}</td>
                    <td className="p-3">
                      {c.shared_with_employee ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`cert-shared-${c.id}`}>
                          <CheckCircle className="h-3 w-3 mr-0.5" /> Sent
                        </Badge>
                      ) : c.employee_id ? (
                        <Badge variant="outline" className="text-[10px]">Not sent</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 text-[10px]">No employee link</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => download(c.id)} data-testid={`cert-download-${c.id}`}>
                        <Download className="h-3.5 w-3.5 mr-1" /> PDF
                      </Button>
                      {c.employee_id && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => openWhatsApp(waNumber(c.employee_id), `Hi ${c.employee_name || ''}, sharing your Experience Certificate. Please find it attached.`)}
                          className="ml-1 text-green-700 border-green-200 hover:bg-green-50"
                          data-testid={`cert-whatsapp-${c.id}`}
                          title="Send via WhatsApp"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <SendReportButton
                        reportUrl={`/api/experience-certificates/${c.id}/pdf`}
                        filename={`Experience_Certificate_${(c.employee_name || '').replace(/\s+/g, '_')}.pdf`}
                        defaultSubject={`Experience Certificate — ${c.employee_name || ''}`}
                        size="sm" iconOnly
                        className="ml-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                        testid={`cert-emailrep-${c.id}`}
                      />
                      {c.employee_id && (
                        <Button size="sm" variant="outline" onClick={() => setEmailCert(c)} className="ml-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-testid={`cert-email-${c.id}`} title="Email certificate to staff">
                          <Mail className="h-3.5 w-3.5 mr-1" /> Email
                        </Button>
                      )}
                      {c.employee_id && (
                        c.shared_with_employee ? (
                          <Button size="sm" variant="ghost" onClick={() => unshare(c.id)} className="ml-1 text-orange-600" data-testid={`cert-unshare-${c.id}`}>
                            Revoke
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => share(c.id)} className="ml-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`cert-share-${c.id}`}>
                            <Send className="h-3.5 w-3.5 mr-1" /> Send
                          </Button>
                        )
                      )}
                      <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="text-rose-600 ml-1" data-testid={`cert-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <EmailDocDialog
        open={Boolean(emailCert)}
        onOpenChange={(o) => { if (!o) setEmailCert(null); }}
        title="Email Employment Certificate"
        recipientName={emailCert?.employee_name}
        attachmentLabel="employment certificate"
        payload={{ doc_type: 'experience_certificate', cert_id: emailCert?.id, employee_id: emailCert?.employee_id }}
      />
    </div>
  );
};

// ── Form ───────────────────────────────────────────────────────────

const CertificateForm = ({ onClose, onCreated, presetEmployeeId, requestId }) => {
  const [employees, setEmployees] = useState([]);
  const [signatories, setSignatories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [form, setForm] = useState({
    employee_id: '',
    salutation: 'Mr.',
    employee_name: '',
    nationality: '',
    passport_no: '',
    emirates_id: '',
    designation: '',
    date_of_joining: '',
    last_working_date: '',
    is_currently_working: false,
    signatory_name: 'Jubu Thankachen',
    signatory_designation: 'Group HR Manager',
    stamp_slug: 'none',
    issue_date: new Date().toISOString().slice(0, 10),
    custom_body: '',
    company: 'stars_n_bars',
  });

  const [companies, setCompanies] = useState([]);
  const [stamps, setStamps] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/employees`).then(r => setEmployees(r.data || []));
    axios.get(`${API}/api/experience-certificates/signatories`).then(r => setSignatories(r.data || []));
    axios.get(`${API}/api/branding/companies`).then(r => setCompanies(r.data || []));
    axios.get(`${API}/api/branding/stamps`).then(r => setStamps(r.data || [])).catch(() => {});
  }, []);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const sortedEmps = useMemo(
    () => [...employees].sort((a, b) => (a.employee_code || '').localeCompare(b.employee_code || '')),
    [employees]
  );

  const onPickEmployee = (id) => {
    const e = employees.find(x => x.id === id);
    if (!e) { setField('employee_id', ''); return; }
    const docs = e.documents || {};
    setForm(prev => ({
      ...prev,
      employee_id: e.id,
      salutation: e.gender === 'Female' ? 'Ms.' : 'Mr.',
      employee_name: e.name || '',
      nationality: e.nationality || '',
      passport_no: docs.passport_number || '',
      emirates_id: docs.emirates_id || '',
      designation: e.position || '',
      date_of_joining: e.join_date || '',
      last_working_date: e.last_working_date || e.exit_date || '',
      is_currently_working: !(e.last_working_date || e.exit_date) && e.employment_status === 'Active',
    }));
  };

  const validate = () => {
    if (!form.employee_name.trim()) return 'Employee name is required';
    if (!form.designation.trim()) return 'Designation is required';
    if (!form.date_of_joining) return 'Date of joining is required';
    if (!form.is_currently_working && !form.last_working_date) return 'Last working date is required (or tick "Currently working")';
    return null;
  };

  // Prefill from a portal document request ("Issue" deep-link).
  useEffect(() => {
    if (presetEmployeeId && employees.length) onPickEmployee(presetEmployeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetEmployeeId, employees]);

  const submit = async () => {
    const err = validate();
    if (err) return toast({ title: err, variant: 'destructive' });
    setSubmitting(true);
    try {
      const body = { ...form, last_working_date: form.is_currently_working ? null : form.last_working_date, stamp_slug: form.stamp_slug === 'none' ? null : form.stamp_slug };
      const res = await axios.post(`${API}/api/experience-certificates`, body);
      const certId = res?.data?.id;
      if (requestId && certId) {
        try {
          await axios.post(`${API}/api/experience-certificates/${certId}/share`);
          await axios.patch(`${API}/api/document-requests/${requestId}`, { status: 'issued', certificate_id: certId });
        } catch (_) { /* linkage best-effort */ }
      }
      toast({ title: 'Certificate generated', description: requestId ? 'Shared with the employee and request marked issued.' : 'Available in the history below.' });
      onCreated();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const preview = async () => {
    const err = validate();
    if (err) return toast({ title: err, variant: 'destructive' });
    setPreviewing(true);
    try {
      const body = { ...form, last_working_date: form.is_currently_working ? null : form.last_working_date, stamp_slug: form.stamp_slug === 'none' ? null : form.stamp_slug };
      const r = await axios.post(`${API}/api/experience-certificates/preview`, body, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch (e) {
      toast({ title: 'Preview failed', variant: 'destructive' });
    } finally { setPreviewing(false); }
  };

  const onSignatoryChange = (name) => {
    const s = signatories.find(x => x.name === name);
    setForm(prev => ({
      ...prev,
      signatory_name: name,
      signatory_designation: s?.designation || prev.signatory_designation,
    }));
  };

  return (
    <Card className="border-amber-200" data-testid="cert-form">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-500" /> New Experience Certificate
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Employee picker */}
        <div>
          <Label className="text-xs">Pick Employee (optional — auto-fills fields)</Label>
          <Select value={form.employee_id || 'manual'} onValueChange={(v) => v === 'manual' ? setField('employee_id', '') : onPickEmployee(v)}>
            <SelectTrigger data-testid="cert-employee-picker">
              <SelectValue placeholder="Manual entry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">— Manual Entry —</SelectItem>
              {sortedEmps.map(e => (
                <SelectItem key={e.id} value={e.id}>
                  {e.employee_code} · {e.name} ({e.position})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Salutation</Label>
            <Select value={form.salutation} onValueChange={(v) => setField('salutation', v)}>
              <SelectTrigger data-testid="cert-salutation"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Mr.">Mr.</SelectItem>
                <SelectItem value="Ms.">Ms.</SelectItem>
                <SelectItem value="Mrs.">Mrs.</SelectItem>
                <SelectItem value="Mx.">Mx.</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Full Name</Label>
            <Input value={form.employee_name} onChange={e => setField('employee_name', e.target.value)} data-testid="cert-name" />
          </div>
          <div>
            <Label className="text-xs">Nationality</Label>
            <Input value={form.nationality} onChange={e => setField('nationality', e.target.value)} data-testid="cert-nationality" />
          </div>
          <div>
            <Label className="text-xs">Designation</Label>
            <Input value={form.designation} onChange={e => setField('designation', e.target.value)} data-testid="cert-designation" />
          </div>
          <div>
            <Label className="text-xs">Passport No.</Label>
            <Input value={form.passport_no} onChange={e => setField('passport_no', e.target.value)} data-testid="cert-passport" />
          </div>
          <div>
            <Label className="text-xs">Emirates ID</Label>
            <Input value={form.emirates_id} onChange={e => setField('emirates_id', e.target.value)} data-testid="cert-eid" />
          </div>
          <div>
            <Label className="text-xs">Date of Joining</Label>
            <Input type="date" value={form.date_of_joining} onChange={e => setField('date_of_joining', e.target.value)} data-testid="cert-doj" />
          </div>
          <div>
            <Label className="text-xs">Issue Date</Label>
            <Input type="date" value={form.issue_date} onChange={e => setField('issue_date', e.target.value)} data-testid="cert-issue-date" />
          </div>
        </div>

        {/* Currently working toggle */}
        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <input
            id="cert-current-toggle"
            type="checkbox"
            checked={form.is_currently_working}
            onChange={e => setField('is_currently_working', e.target.checked)}
            className="rounded border-gray-300"
            data-testid="cert-currently-working"
          />
          <label htmlFor="cert-current-toggle" className="text-sm font-medium select-none cursor-pointer">
            Employee is currently working
          </label>
        </div>

        {!form.is_currently_working && (
          <div>
            <Label className="text-xs">Last Working Date</Label>
            <Input type="date" value={form.last_working_date} onChange={e => setField('last_working_date', e.target.value)} data-testid="cert-lwd" />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Company Letterhead</Label>
            <Select value={form.company} onValueChange={(v) => setField('company', v)}>
              <SelectTrigger data-testid="cert-company"><SelectValue placeholder="Select company" /></SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.slug} value={c.slug}>{c.name}{c.is_default ? ' (default)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-gray-400 mt-1">Header &amp; footer for the selected company are used. Pick a stamp separately below.</p>
          </div>
          <div>
            <Label className="text-xs">Issue Date</Label>
            <Input type="date" value={form.issue_date} onChange={e => setField('issue_date', e.target.value)} data-testid="cert-issue-date-2" />
          </div>
          <div>
            <Label className="text-xs">Company Stamp</Label>
            <Select value={form.stamp_slug} onValueChange={(v) => setField('stamp_slug', v)}>
              <SelectTrigger data-testid="cert-stamp"><SelectValue placeholder="No stamp" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No stamp —</SelectItem>
                {stamps.map(st => (
                  <SelectItem key={st.slug} value={st.slug}>{st.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-gray-400 mt-1">Manage stamps in Settings → Branding.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Signatory</Label>
            <Select value={form.signatory_name} onValueChange={onSignatoryChange}>
              <SelectTrigger data-testid="cert-signatory"><SelectValue /></SelectTrigger>
              <SelectContent>
                {signatories.map(s => (
                  <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Signatory Designation</Label>
            <Input value={form.signatory_designation} onChange={e => setField('signatory_designation', e.target.value)} data-testid="cert-signatory-role" />
          </div>
        </div>

        <div>
          <Label className="text-xs">Custom Body (optional — overrides default text)</Label>
          <Textarea
            rows={5}
            value={form.custom_body}
            onChange={e => setField('custom_body', e.target.value)}
            placeholder="Leave empty to auto-generate the standard testimonial paragraphs."
            data-testid="cert-custom-body"
          />
          <p className="text-[10px] text-gray-400 mt-1">Separate paragraphs with a blank line.</p>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" variant="outline" onClick={preview} disabled={previewing} data-testid="cert-preview">
            <Eye className="h-4 w-4 mr-1" /> {previewing ? 'Generating…' : 'Preview PDF'}
          </Button>
          <Button type="button" onClick={submit} disabled={submitting} data-testid="cert-save">
            <FileText className="h-4 w-4 mr-1" /> {submitting ? 'Saving…' : 'Generate & Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExperienceCertificate;
